import { useTranslation } from '@/lib/i18n';
import { Input, RadioGroup } from '@/components/ui';
import type { RefundScope, RefundScopeItem, RefundScopeState } from '../lib/refundScope';

export interface RefundScopeFieldsProps {
  idPrefix: string;
  items: ReadonlyArray<RefundScopeItem>;
  state: RefundScopeState;
  onChange: (next: RefundScopeState) => void;
  error?: 'scopeRequired' | 'itemsRequired' | null;
}

/**
 * QA-625 환불 구분(상품 / 상품 외) + 상품 환불이면 상품·수량 선택. 수량 한도는 BE refundableQuantity.
 * 환불할 수 있는 상품이 없으면(전부 취소·환불됐거나 BE 미반영) 상품 환불은 고를 수 없다.
 */
export const RefundScopeFields = ({ idPrefix, items, state, onChange, error = null }: RefundScopeFieldsProps) => {
  const { t } = useTranslation('payments');
  const labelId = `${idPrefix}-scope-label`;
  const noItems = items.length === 0;

  const setQuantity = (orderItemId: string, raw: number, max: number) => {
    const qty = Number.isFinite(raw) ? Math.min(Math.max(Math.trunc(raw), 0), max) : 0;
    onChange({ ...state, quantities: { ...state.quantities, [orderItemId]: qty } });
  };

  return (
    <div className="flex flex-col gap-2" data-testid={`${idPrefix}-scope`}>
      <span id={labelId} className="text-sm text-[color:var(--content-muted)]">
        {t('payments.refundScope.label', '환불 구분')}
      </span>
      <RadioGroup
        value={state.scope ?? ''}
        onValueChange={(v) => onChange({ ...state, scope: v as RefundScope })}
        options={[
          { value: 'NON_PRODUCT', label: t('payments.refundScope.NON_PRODUCT', '상품 외 환불(배송비·차액 등)') },
          { value: 'PRODUCT', label: t('payments.refundScope.PRODUCT', '상품 환불'), disabled: noItems },
        ]}
        orientation="horizontal"
        aria-labelledby={labelId}
        data-testid={`${idPrefix}-scope-radio`}
      />
      <p className="text-xs text-[color:var(--content-muted)]">
        {noItems
          ? t('payments.refundScope.noItems', '환불할 수 있는 상품이 없어 상품 외 환불만 할 수 있습니다.')
          : t('payments.refundScope.hint', '상품 외 환불은 주문·상품 상태를 바꾸지 않습니다. 상품 환불은 재고·마일리지·쿠폰을 되돌리지 않습니다.')}
      </p>

      {state.scope === 'PRODUCT' ? (
        <ul className="space-y-1.5 rounded-md border p-2" style={{ borderColor: 'var(--surface-1)' }} data-testid={`${idPrefix}-items`}>
          {items.map((item) => {
            const inputId = `${idPrefix}-item-${item.orderItemId}`;
            return (
              <li key={item.orderItemId} className="flex items-center gap-2 text-sm">
                <label htmlFor={inputId} className="min-w-0 flex-1 text-[color:var(--content-strong)]">
                  {item.productName}
                  {item.optionLabel ? (
                    <span className="ml-1 text-[color:var(--content-muted)]">{item.optionLabel}</span>
                  ) : null}
                </label>
                <Input
                  id={inputId}
                  type="number"
                  min={0}
                  max={item.refundableQuantity}
                  step={1}
                  className="w-20"
                  value={state.quantities[item.orderItemId] ?? 0}
                  onChange={(e) => setQuantity(item.orderItemId, e.target.valueAsNumber, item.refundableQuantity)}
                  data-testid={`${idPrefix}-qty-${item.orderItemId}`}
                />
                <span className="whitespace-nowrap text-xs text-[color:var(--content-muted)]">
                  {t('payments.refundScope.maxQty', { count: item.refundableQuantity, defaultValue: '/ {{count}}개' })}
                </span>
              </li>
            );
          })}
        </ul>
      ) : null}

      {error ? (
        <p className="text-xs" role="alert" style={{ color: 'var(--status-danger-fg)' }} data-testid={`${idPrefix}-scope-error`}>
          {error === 'scopeRequired'
            ? t('payments.refundScope.scopeRequired', '환불 구분을 골라 주세요.')
            : t('payments.refundScope.itemsRequired', '환불할 상품과 수량을 골라 주세요.')}
        </p>
      ) : null}
    </div>
  );
};
